import React from 'react'
import { Paper, Box, IconButton, Tooltip, Divider, Typography, Button, FormControlLabel, Switch } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import TuneIcon from '@mui/icons-material/Tune'
import BarChartIcon from '@mui/icons-material/BarChart'
import InfoIcon from '@mui/icons-material/Info'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseIcon from '@mui/icons-material/Close'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import { TableViewSettingsData, TableVisibilitySettings } from '../../types/app'
import QuickFilterChip from './QuickFilterChip'
import {
  MIN_HORIZONTAL_COEFFICIENT,
  MAX_HORIZONTAL_COEFFICIENT,
  MIN_VERTICAL_COEFFICIENT,
  MAX_VERTICAL_COEFFICIENT,
  COEFFICIENT_STEP
} from './styles'

interface TableControlsProps {
  viewSettings: TableViewSettingsData;
  onViewSettingsChange: (settings: Partial<TableViewSettingsData>) => void;
  onVisibilitySettingsChange?: (settings: Partial<TableVisibilitySettings>) => void;
  hiddenProperties?: Partial<Record<'frequency' | 'column_percent' | 'row_percent' | 'significance', boolean>>;
  fullscreen: boolean;
  onFullscreenToggle: () => void;
  onChartView?: () => void;
  onInfoClick?: () => void;
  // Download the table as an Excel workbook; the button shows only when given
  onDownloadXlsx?: () => void;
  isXLSX?: boolean;
  isDebug?: boolean;
  // Cache control props (for managers only)
  showCacheSwitch?: boolean;
  useCache?: boolean;
  onCacheToggle?: (useCache: boolean) => void;
  // Quick filter props
  filter?: string;
  onQuickFilter?: () => void;
  onClearFilter?: () => void;
  // Settings drawer
  onSettings?: () => void;
  // Significance data availability
  hasSigData?: boolean;
  // Side labels mode: full → hide-groups → hide-all
  sideLabelsMode?: 'full' | 'hide-groups' | 'hide-all';
  onCompactSideLabelsToggle?: () => void;
}

export const TableControls: React.FC<TableControlsProps> = ({
  viewSettings,
  onViewSettingsChange,
  onVisibilitySettingsChange,
  hiddenProperties,
  fullscreen,
  onFullscreenToggle,
  onChartView,
  onInfoClick,
  onDownloadXlsx,
  isXLSX = false,
  isDebug = false,
  showCacheSwitch = false,
  useCache = true,
  onCacheToggle,
  filter,
  onQuickFilter,
  onClearFilter,
  onSettings,
  hasSigData,
  sideLabelsMode = 'full',
  onCompactSideLabelsToggle,
}) => {
  // Row height / column width controls — re-added per Matt's request
  const handleExpandVertical = () => {
    const newValue = Math.min(viewSettings.vertical_coefficient + COEFFICIENT_STEP, MAX_VERTICAL_COEFFICIENT)
    onViewSettingsChange({ vertical_coefficient: newValue })
  }

  const handleContractVertical = () => {
    const newValue = Math.max(viewSettings.vertical_coefficient - COEFFICIENT_STEP, MIN_VERTICAL_COEFFICIENT)
    onViewSettingsChange({ vertical_coefficient: newValue })
  }

  const handleExpandHorizontal = () => {
    const newValue = Math.min(viewSettings.horizontal_coefficient + COEFFICIENT_STEP, MAX_HORIZONTAL_COEFFICIENT)
    onViewSettingsChange({ horizontal_coefficient: newValue })
  }

  const handleContractHorizontal = () => {
    const newValue = Math.max(viewSettings.horizontal_coefficient - COEFFICIENT_STEP, MIN_HORIZONTAL_COEFFICIENT)
    onViewSettingsChange({ horizontal_coefficient: newValue })
  }

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 1.5,
        py: 0.5,
        borderRadius: 0,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Left side: Action icons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>

        {!fullscreen && (
          <>
            <Tooltip title="Chart View" placement="top">
              <span>
                <IconButton
                  aria-label="Chart View"
                  onClick={onChartView}
                  size="small"
                  disabled={!onChartView}
                  sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '50%', color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' } }}
                >
                  <BarChartIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Metadata" placement="top">
              <span>
                <IconButton
                  aria-label="Metadata"
                  onClick={onInfoClick}
                  size="small"
                  sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '50%', color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' } }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

          </>
        )}

        {onDownloadXlsx && (
          <Tooltip title="Download to Excel" placement="top">
            <IconButton
              aria-label="Download to Excel"
              onClick={onDownloadXlsx}
              size="small"
              sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '50%', color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' } }}
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={fullscreen ? "Close Fullscreen" : "Enter Fullscreen"} placement="top">
          <IconButton
            aria-label={fullscreen ? "Close Fullscreen" : "Enter Fullscreen"}
            onClick={onFullscreenToggle}
            size="small"
            sx={{
              p: 0.5,
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.03)',
                borderColor: 'rgba(0,0,0,0.15)',
              },
            }}
          >
            {fullscreen ? <CloseIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {/* Row height & column width controls — re-added per Matt's request */}
        {!isXLSX && !isDebug && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            <Tooltip title="Contract Header Height" placement="top">
              <span>
                <IconButton
                  aria-label="Contract Header Height"
                  onClick={handleContractVertical}
                  size="small"
                  disabled={viewSettings.vertical_coefficient <= MIN_VERTICAL_COEFFICIENT}
                  sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '50%', color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' } }}
                >
                  <UnfoldLessIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Expand Header Height" placement="top">
              <span>
                <IconButton
                  aria-label="Expand Header Height"
                  onClick={handleExpandVertical}
                  size="small"
                  disabled={viewSettings.vertical_coefficient >= MAX_VERTICAL_COEFFICIENT}
                  sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '50%', color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' } }}
                >
                  <UnfoldMoreIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Contract Header Width" placement="top">
              <span>
                <IconButton
                  aria-label="Contract Header Width"
                  onClick={handleContractHorizontal}
                  size="small"
                  disabled={viewSettings.horizontal_coefficient <= MIN_HORIZONTAL_COEFFICIENT || sideLabelsMode !== 'full'}
                  sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '50%', color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' } }}
                >
                  <UnfoldLessIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Expand Header Width" placement="top">
              <span>
                <IconButton
                  aria-label="Expand Header Width"
                  onClick={handleExpandHorizontal}
                  size="small"
                  disabled={viewSettings.horizontal_coefficient >= MAX_HORIZONTAL_COEFFICIENT || sideLabelsMode !== 'full'}
                  sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '50%', color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' } }}
                >
                  <UnfoldMoreIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
                </IconButton>
              </span>
            </Tooltip>

            {onCompactSideLabelsToggle && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Tooltip title={
                  sideLabelsMode === 'full' ? 'Hide Group Labels' :
                  sideLabelsMode === 'hide-groups' ? 'Hide All Side Labels' :
                  'Show All Side Labels'
                } placement="top">
                  <IconButton
                    aria-label={
                      sideLabelsMode === 'full' ? 'Hide Group Labels' :
                      sideLabelsMode === 'hide-groups' ? 'Hide All Side Labels' :
                      'Show All Side Labels'
                    }
                    onClick={onCompactSideLabelsToggle}
                    size="small"
                    sx={{
                      p: 0.5,
                      border: '1px solid',
                      borderColor: sideLabelsMode !== 'full' ? 'primary.main' : 'divider',
                      borderRadius: '50%',
                      color: sideLabelsMode !== 'full' ? 'primary.main' : 'text.secondary',
                      backgroundColor: sideLabelsMode !== 'full' ? 'rgba(30, 45, 77, 0.06)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderColor: 'rgba(0,0,0,0.15)' },
                    }}
                  >
                    <ViewColumnIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          </>
        )}

      </Box>

      {/* Right side: Quick filter + Display options */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>

        {(onQuickFilter || onSettings) && (
          <>
            {onQuickFilter && (
              <Button
                startIcon={<FilterListIcon />}
                onClick={onQuickFilter}
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  color: 'grey.600',
                  mr: 0.5,
                }}
              >
                Quick Filter
              </Button>
            )}
            {onQuickFilter && filter && <QuickFilterChip filter={filter} onClear={onClearFilter} />}
            {onSettings && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Button
                  startIcon={<TuneIcon />}
                  onClick={onSettings}
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: 'grey.600',
                    mr: 0.5,
                  }}
                >
                  Quick Settings
                </Button>
              </>
            )}
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          </>
        )}

        {showCacheSwitch && (
          <Tooltip title={useCache ? "Cache enabled - toggle to bypass cache" : "Cache bypassed - data loaded fresh from server"}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCache}
                  onChange={() => onCacheToggle?.(!useCache)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4caf50',
                      '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4caf50',
                    },
                  }}
                />
              }
              label={<Typography variant="caption">Cache</Typography>}
              sx={{ m: 0 }}
            />
          </Tooltip>
        )}
        
      </Box>
    </Paper>
  )
}

export default TableControls
